import Matter from "matter-js";

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

/**
 * Crea e avvia un motore fisico Matter.js su un singolo stage (slide attiva).
 * Usa Runner + evento afterUpdate per tenere il DOM allineato allo step
 * fisico (niente requestAnimationFrame separato: evita scatti/tremolii).
 *
 * Ritorna una funzione di cleanup da chiamare al cambio slide.
 *
 * @param {HTMLElement} stage
 * @param {Object} config
 */
function initStagePhysics(stage, config) {
  const objectsContainer = stage.querySelector(
    ".cappelli-team-slider__objects",
  );
  const portraitEl = stage.querySelector(".cappelli-team-slider__portrait");

  if (!objectsContainer) {
    return () => {};
  }

  const imgs = Array.from(objectsContainer.querySelectorAll("img"));

  if (imgs.length === 0) {
    return () => {};
  }

  const bounds = stage.getBoundingClientRect();
  const W = Math.round(bounds.width);
  const H = Math.round(bounds.height);

  // Stage non ancora dimensionato (o vista griglia attiva, quindi slider
  // in display:none): non avviare, i muri finirebbero nel posto sbagliato.
  // Il chiamante riproverà al resize o al prossimo cambio vista/slide.
  if (W === 0 || H === 0) {
    return () => {};
  }

  const Engine = Matter.Engine,
    Runner = Matter.Runner,
    World = Matter.World,
    Bodies = Matter.Bodies,
    Mouse = Matter.Mouse,
    MouseConstraint = Matter.MouseConstraint,
    Body = Matter.Body,
    Events = Matter.Events;

  const engine = Engine.create();
  engine.gravity.y = config.gravity ?? 0.7;
  engine.gravity.scale = 0.001;
  engine.enableSleeping = true;
  // Più iterazioni = collisioni più solide, meno probabilità che un
  // corpo trascinato con forza contro un muro lo attraversi (tunneling).
  engine.positionIterations = 10;
  engine.velocityIterations = 10;
  const world = engine.world;

  const runner = Runner.create();

  const wallOpts = { isStatic: true, restitution: 0.3, friction: 0.6 };
  // Muri molto spessi: un corpo trascinato con forza non riesce ad
  // attraversarli anche se penetra di qualche pixel.
  const thickness = 200;
  World.add(world, [
    Bodies.rectangle(
      W / 2,
      -thickness / 2,
      W + thickness * 2,
      thickness,
      wallOpts,
    ),
    Bodies.rectangle(
      W / 2,
      H + thickness / 2,
      W + thickness * 2,
      thickness,
      wallOpts,
    ),
    Bodies.rectangle(
      -thickness / 2,
      H / 2,
      thickness,
      H + thickness * 2,
      wallOpts,
    ),
    Bodies.rectangle(
      W + thickness / 2,
      H / 2,
      thickness,
      H + thickness * 2,
      wallOpts,
    ),
  ]);

  // Ostacolo statico invisibile che replica la featured image al centro:
  // gli oggetti ci si scontrano contro invece di sovrapporla.
  if (portraitEl) {
    const stageRect = stage.getBoundingClientRect();
    const portraitRect = portraitEl.getBoundingClientRect();
    const cx = portraitRect.left - stageRect.left + portraitRect.width / 2;
    const cy = portraitRect.top - stageRect.top + portraitRect.height / 2;
    const radius =
      (Math.min(portraitRect.width, portraitRect.height) / 2) * 0.9;

    if (radius > 0) {
      World.add(
        world,
        Bodies.circle(cx, cy, radius, {
          isStatic: true,
          restitution: 0.4,
        }),
      );
    }
  }

  // Tetto massimo per il lato lungo, così un'immagine molto grande
  // non riempie tutto lo stage.
  //const maxLongSide = Math.round(Math.min(W, H) * 0.3);
  // Fattore diverso su mobile: 782px = sopra il breakpoint mobile di WP.
  const isDesktop = window.matchMedia("(min-width: 782px)").matches;
  const longSideFactor = isDesktop ? 0.3 : 0.15;
  const maxLongSide = Math.round(Math.min(W, H) * longSideFactor);

  const items = imgs.map((img) => {
    const naturalW = parseFloat(img.dataset.width) || 200;
    const naturalH = parseFloat(img.dataset.height) || 200;

    let w = naturalW;
    let h = naturalH;

    const longSide = Math.max(w, h);
    if (longSide > maxLongSide) {
      const scale = maxLongSide / longSide;
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    img.style.position = "absolute";
    img.style.top = "0";
    img.style.left = "0";
    img.style.width = w + "px";
    img.style.height = h + "px";
    img.style.objectFit = "cover";
    img.style.willChange = "transform";
    img.style.userSelect = "none";
    img.style.webkitUserDrag = "none";
    img.draggable = false;
    img.addEventListener("dragstart", (e) => e.preventDefault());

    const margin = 20;
    const x = margin + Math.random() * Math.max(1, W - margin * 2);
    const y = margin + Math.random() * (H / 2);

    const body = Bodies.rectangle(x, y, w, h, {
      restitution: 0.4,
      friction: 0.4,
      frictionAir: 0.02,
      density: 0.001,
      chamfer: { radius: Math.min(w, h) * 0.12 },
      sleepThreshold: 30,
    });

    Body.setVelocity(body, {
      x: (Math.random() * 2 - 1) * 1.5,
      y: 0,
    });
    Body.setAngularVelocity(body, (Math.random() * 2 - 1) * 0.03);

    World.add(world, body);

    return { el: img, body, w, h };
  });

  const mouse = Mouse.create(stage);
  mouse.pixelRatio = 1;

  // Matter.js registra listener su wheel/mousewheel e ne fa
  // preventDefault(), bloccando lo scroll della pagina quando il
  // puntatore è sopra lo stage. Non ci serve lo zoom, quindi li
  // rimuoviamo per lasciar scorrere la pagina normalmente.
  if (mouse.mousewheel) {
    stage.removeEventListener("wheel", mouse.mousewheel);
    stage.removeEventListener("mousewheel", mouse.mousewheel);
    stage.removeEventListener("DOMMouseScroll", mouse.mousewheel);
  }

  const mouseConstraint = MouseConstraint.create(engine, {
    mouse,
    constraint: { stiffness: 0.2, render: { visible: false } },
  });
  World.add(world, mouseConstraint);

  // Clamp dell'ancora del mouse entro i bordi dello stage.
  const clampMouse = () => {
    const p = mouseConstraint.mouse.position;
    if (p.x < 0) {
      p.x = 0;
    } else if (p.x > W) {
      p.x = W;
    }
    if (p.y < 0) {
      p.y = 0;
    } else if (p.y > H) {
      p.y = H;
    }
  };
  Events.on(engine, "beforeUpdate", clampMouse);

  const onMouseDown = () => {
    objectsContainer.style.cursor = "grabbing";
  };
  const onMouseUp = () => {
    objectsContainer.style.cursor = "grab";
  };
  objectsContainer.style.cursor = "grab";
  stage.addEventListener("mousedown", onMouseDown);
  stage.addEventListener("mouseup", onMouseUp);

  // Rilascio del drag anche fuori dallo stage.
  const endDrag = () => {
    if (mouseConstraint.body) {
      mouseConstraint.constraint.bodyB = null;
      mouseConstraint.body = null;
    }
    mouse.button = -1;
    objectsContainer.style.cursor = "grab";
  };
  window.addEventListener("mouseup", endDrag);
  window.addEventListener("touchend", endDrag);
  stage.addEventListener("mouseleave", endDrag);

  // Sincronizzazione DOM agganciata allo step fisico.
  let revealed = false;
  const escapeMargin = 80;
  const syncDom = () => {
    for (const item of items) {
      // Rete di sicurezza: se un corpo è comunque sfuggito oltre i
      // bordi, lo riporto dentro azzerandone la velocità.
      const p = item.body.position;
      if (
        p.x < -escapeMargin ||
        p.x > W + escapeMargin ||
        p.y < -escapeMargin ||
        p.y > H + escapeMargin
      ) {
        Body.setPosition(item.body, { x: W / 2, y: H / 3 });
        Body.setVelocity(item.body, { x: 0, y: 0 });
        Body.setAngularVelocity(item.body, 0);
      }

      const pos = item.body.position;
      const angle = item.body.angle;
      item.el.style.transform =
        "translate(" +
        (pos.x - item.w / 2) +
        "px," +
        (pos.y - item.h / 2) +
        "px) rotate(" +
        angle +
        "rad)";
    }
    if (!revealed) {
      revealed = true;
      objectsContainer.classList.add("is-ready");
    }
  };
  Events.on(runner, "afterUpdate", syncDom);

  Runner.run(runner, engine);

  return function cleanup() {
    Events.off(runner, "afterUpdate", syncDom);
    Events.off(engine, "beforeUpdate", clampMouse);
    Runner.stop(runner);
    stage.removeEventListener("mousedown", onMouseDown);
    stage.removeEventListener("mouseup", onMouseUp);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchend", endDrag);
    stage.removeEventListener("mouseleave", endDrag);
    objectsContainer.classList.remove("is-ready");
    World.clear(world, false);
    Engine.clear(engine);
  };
}

/**
 * Attende che il file JS globale del tema abbia inizializzato
 * new Swiper(".teamslider", {...}) su questo elemento, poi
 * restituisce l'istanza tramite el.swiper.
 *
 * @param {HTMLElement} el
 * @param {Function} callback
 */
function waitForSwiperInstance(el, callback) {
  if (el.swiper) {
    callback(el.swiper);
    return;
  }
  const check = setInterval(() => {
    if (el.swiper) {
      clearInterval(check);
      callback(el.swiper);
    }
  }, 50);
}

/**
 * Gestisce un singolo blocco: switch griglia/slider + fisica.
 *
 * @param {HTMLElement} el Wrapper .cappelli-team-slider
 */
function initTeamSlider(el) {
  const gridEl = el.querySelector(".cappelli-team-slider__grid");
  const sliderEl = el.querySelector(".cappelli-team-slider__slider");
  const viewButtons = Array.from(
    el.querySelectorAll(".cappelli-team-slider__view-btn"),
  );

  if (!gridEl || !sliderEl) {
    return;
  }

  let config = {};
  try {
    config = JSON.parse(el.dataset.physicsConfig || "{}");
  } catch (e) {
    config = {};
  }

  let currentView = el.dataset.view || "grid";
  let swiper = null;
  let cleanupCurrent = () => {};

  /**
   * Riempie nome/ruolo dallo slide attivo. Indipendente da `slideChange`:
   * quando si apre lo slider sulla PRIMA slide (grid index 0 o toggle) non
   * avviene alcun cambio slide, quindi il JS globale non riempirebbe
   * l'info. La aggiorniamo noi a ogni rivelazione dello slider.
   */
  function updateSlideInfo() {
    if (!swiper) {
      return;
    }
    const slide = swiper.slides[swiper.activeIndex];
    if (!slide) {
      return;
    }
    const nameEl = sliderEl.querySelector(".cappelli-team-slider__name");
    const roleEl = sliderEl.querySelector(".cappelli-team-slider__role");
    if (nameEl) {
      nameEl.textContent = slide.dataset.name || "";
    }
    if (roleEl) {
      roleEl.textContent = slide.dataset.role || "";
    }
  }

  /**
   * (Ri)avvia la fisica sulla slide attiva. No-op se la vista corrente
   * non è lo slider: lo stage sarebbe in display:none e misurerebbe 0x0.
   */
  function activate() {
    cleanupCurrent();
    cleanupCurrent = () => {};

    if (currentView !== "slider" || prefersReducedMotion) {
      return;
    }

    const activeStage = sliderEl.querySelector(
      ".swiper-slide-active .cappelli-team-slider__stage",
    );

    if (activeStage) {
      cleanupCurrent = initStagePhysics(activeStage, config);
    }
  }

  /**
   * @param {'grid'|'slider'} view
   * @param {number} [index] Slide su cui aprire lo slider.
   */
  function setView(view, index) {
    currentView = view;
    el.dataset.view = view;

    const isSlider = view === "slider";

    gridEl.classList.toggle("is-hidden", isSlider);
    gridEl.setAttribute("aria-hidden", isSlider ? "true" : "false");
    sliderEl.classList.toggle("is-hidden", !isSlider);
    sliderEl.setAttribute("aria-hidden", isSlider ? "false" : "true");

    viewButtons.forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    if (!isSlider) {
      // Fermo il motore fisico: niente Runner attivo su uno stage nascosto.
      cleanupCurrent();
      cleanupCurrent = () => {};
      return;
    }

    // Se apro lo slider partendo da un membro in fondo alla griglia, il
    // blocco inizia più in alto della posizione a cui avevo scrollato:
    // la parte alta dello slider resterebbe sopra la viewport, tagliata.
    // Riporto il blocco in cima alla viewport.
    el.scrollIntoView({ block: "start", behavior: "smooth" });

    if (!swiper) {
      // L'istanza non è ancora pronta: ci penserà waitForSwiperInstance.
      return;
    }

    // Swiper è stato inizializzato mentre lo slider era in display:none,
    // quindi larghezze/traslazioni sono a 0. update() le ricalcola ora
    // che l'elemento è visibile.
    swiper.update();

    if (typeof index === "number" && index !== swiper.activeIndex) {
      swiper.slideTo(index, 0);
    }

    // Riempio nome/ruolo anche quando non è avvenuto alcun slideChange
    // (prima slide: grid index 0 o toggle).
    updateSlideInfo();

    // Un frame di attesa: il layout deve essere applicato prima che
    // initStagePhysics misuri lo stage.
    requestAnimationFrame(activate);
  }

  waitForSwiperInstance(sliderEl, (swiperInstance) => {
    swiper = swiperInstance;
    swiper.on("slideChangeTransitionEnd", () => {
      updateSlideInfo();
      activate();
    });

    // Se nel frattempo l'utente è già passato allo slider (click veloce
    // prima che il JS globale inizializzasse Swiper), allineo lo stato.
    if (currentView === "slider") {
      setView("slider");
    }
  });

  viewButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      setView(btn.dataset.view);
    });
  });

  gridEl.addEventListener("click", (e) => {
    const item = e.target.closest(".cappelli-team-slider__grid-item");
    if (!item) {
      return;
    }
    const index = parseInt(item.dataset.index, 10);
    setView("slider", Number.isNaN(index) ? 0 : index);
  });

  let resizeTimer = null;
  window.addEventListener("resize", () => {
    if (currentView !== "slider") {
      return;
    }
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(activate, 300);
  });
}

document.querySelectorAll(".cappelli-team-slider").forEach(initTeamSlider);
